// Phase P.32 — per-manager, per-page filter persistence.
//
// Stores an arbitrary filter object under `jbk:{pageKey}:filters` in
// localStorage with a 24h TTL. Restores on mount, wipes on
// `clearPersistedFilters`. Silently no-ops when localStorage is
// unavailable (Safari private mode, disabled storage, etc.).
//
// Usage:
//   const [filters, setFilters] = usePersistedFilters("jobs", {
//     status: "all", from: "", to: "",
//   });
//   // …later:
//   const reset = () => setFilters(DEFAULT_FILTERS);   // and it persists.
//
// Notes:
//   * The full filters object is stored; consumers keep supplying the
//     schema they want. Any keys the caller stops using naturally
//     disappear next time they write.
//   * TTL is enforced on READ — an expired blob is dropped and the
//     hook returns the passed-in default.

import { useEffect, useRef, useState } from "react";

const NAMESPACE = "jbk";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

function _key(pageKey) {
  return `${NAMESPACE}:${pageKey}:filters`;
}

function _safeParse(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function readPersisted(pageKey) {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(_key(pageKey));
    const blob = _safeParse(raw);
    if (!blob || typeof blob !== "object") return null;
    if (!blob.savedAt || Date.now() - Number(blob.savedAt) > TTL_MS) {
      window.localStorage.removeItem(_key(pageKey));
      return null;
    }
    return blob.filters ?? null;
  } catch { return null; }
}

function writePersisted(pageKey, filters) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(_key(pageKey), JSON.stringify({
      savedAt: Date.now(),
      filters,
    }));
  } catch { /* quota exhausted or blocked — ignore */ }
}

export function clearPersistedFilters(pageKey) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try { window.localStorage.removeItem(_key(pageKey)); } catch { /* ignore */ }
}

/**
 * useState-shaped hook that merges an on-disk snapshot with the caller's
 * defaults, and writes on every change.
 *
 * @param {string} pageKey e.g. "jobs" | "accounts" | "customers" | "calendar"
 * @param {object} defaults default filter object (also acts as the schema)
 * @returns {[filters, setFilters, resetFilters]}
 */
export function usePersistedFilters(pageKey, defaults) {
  // Only run the read once — subsequent renders use component state.
  const bootRef = useRef(null);
  if (bootRef.current === null) {
    const snap = readPersisted(pageKey);
    bootRef.current = snap
      ? { ...defaults, ...snap }
      : { ...defaults };
  }
  const [filters, setFiltersState] = useState(bootRef.current);

  // Persist on every update.
  useEffect(() => {
    writePersisted(pageKey, filters);
  }, [pageKey, filters]);

  const setFilters = (next) => {
    if (typeof next === "function") {
      setFiltersState((prev) => next(prev));
    } else {
      setFiltersState(next);
    }
  };

  const resetFilters = () => {
    clearPersistedFilters(pageKey);
    setFiltersState({ ...defaults });
  };

  return [filters, setFilters, resetFilters];
}

export default usePersistedFilters;
